// ⬇️ Export this chat (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.10; Nina 09-29: "Export chats
// feature (should it also export docs? And ghost chat? Or should that be a toggle on export
// dialog)"). Mockup: room-toolkit.html §5 (one dialog, toggles, Markdown or JSON).
//
// The rule: the file equals what this viewer could already scroll, and nothing more.
//   · The ONLY source is the paged reader GET /chat-game/messages (≤200 a page, a `before`
//     cursor, type chat | spectating, the viewer's seat proven by its secret: readViewer).
//     Never getGameTranscript (unbounded get_actions, ignores hidden-by-reports) and never
//     page state (allActions is a rolling ~100-row window).
//   · Every line is REBUILT from a whitelist (who, time, text, a GIF, a file's name +
//     summary + link, a thought). The endpoint spreads each row's data whole (sender_id,
//     recipient_ids, client_msg_id, extracted_text, strokes…); none of that is copied, so no
//     seat id, secret, IP or document body can ride out, whatever the endpoint sends.
//   · Only your own DMs: the endpoint answers another seat's DM as a "private_hidden" stub,
//     which is dropped; a DM reaches the file only when the server proved you are in it.
//   · A line hidden after community reports exports as "(hidden after reports)" whatever
//     text rides with it (the live status read still serves some of those in full: spec §7).
//   · Side chat (the ghost crowd) is OFF by default and never offered in competitive /
//     imposter rooms (a secret channel there). Couch quips (is_watch_quip / is_stream_quip)
//     are video overlays, not side chat, and stay out.
//   · AI thoughts are OFF by default: they ride public rows and the room's "AI Thoughts"
//     switch shows them to anyone today. Never offered in competitive / imposter rooms, and
//     no "(AI)" marks there either (who is an AI can be the game).
// Pure but for downloadText (a Blob + a temporary <a download>, the memoryExport idiom).

export const EXPORT_LINE_CAP = 5000;
export const EXPORT_PAGE_SIZE = 200;
// A room can be long enough that a Custom range deep in the past means paging through a lot
// of newer rows first: stop after this many pages per stream and say so in the file.
export const EXPORT_MAX_PAGES = 100;
export const EXPORT_FOOTER = 'Other people’s words are in here too. Be kind with them.';
export const EXPORT_PRIVACY_LINE = 'Only your own DMs. Lines hidden after reports stay hidden.';
export const HIDDEN_LINE = '(hidden after reports)';
const DAY_MS = 24 * 60 * 60 * 1000;

export const EXPORT_DEFAULTS = Object.freeze({
  main: true, side: false, files: true, thoughts: false, range: 'week', format: 'md',
});

// ── small text helpers ──────────────────────────────────────────────────────────
const oneLine = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
const clamp = (s, n) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
const nbspToSpace = (s) => String(s).replace(/[\u00a0\u202f]/g, ' ');

/** http(s) only, no control characters; spaces, parens and angle brackets encoded so a Markdown link can't break. */
export function safeUrl(u) {
  const s = String(u == null ? '' : u).trim();
  // eslint-disable-next-line no-control-regex
  if (!/^https?:\/\/\S/i.test(s) || /[\u0000-\u001f\u007f]/.test(s) || s.length > 2048) return null;
  return s.replace(/ /g, '%20').replace(/\(/g, '%28').replace(/\)/g, '%29').replace(/</g, '%3C').replace(/>/g, '%3E');
}

const nameOf = (...cands) => {
  for (const c of cands) {
    const n = clamp(oneLine(c), 60);
    if (n && n !== 'Unknown') return n;
  }
  return 'Someone';
};

/** A row's time in ms: the endpoint's `timestamp` (an ISO created_at, or the data's own ms stamp). */
export function tsOf(m) {
  if (!m) return null;
  for (const v of [m.timestamp, m.created_at]) {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v) {
      const n = /^\d{10,}$/.test(v) ? Number(v) : Date.parse(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

// ── range ───────────────────────────────────────────────────────────────────────
const localDay = (ymd, endOfDay) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return null;
  const d = endOfDay
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999)
    : new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
  return Number.isFinite(d.getTime()) ? d.getTime() : null;
};

/**
 * { fromMs, toMs } for a range: 'week' = the last 7 days · 'all' = no bounds ·
 * 'custom' = from / to as 'YYYY-MM-DD' days in the viewer's zone (whole days, swapped if
 * backwards). A custom range with a missing or bad day → null (the dialog asks for both).
 */
export function rangeBounds(range, { now = Date.now(), from = null, to = null } = {}) {
  if (range === 'all') return { fromMs: null, toMs: null };
  if (range === 'custom') {
    let a = localDay(from, false);
    let b = localDay(to, true);
    if (a == null || b == null) return null;
    if (a > b) { a = localDay(to, false); b = localDay(from, true); }
    return { fromMs: a, toMs: b };
  }
  return { fromMs: now - 7 * DAY_MS, toMs: null };
}

// ── one row → one line (or null) ───────────────────────────────────────────────
const FILE_LABELS = {
  pdf: 'A PDF', document: 'A document', image: 'A picture', image_url: 'A picture', picture: 'A picture',
  video: 'A video', video_url: 'A video', drawing: 'A drawing', doodle: 'A drawing', youtube: 'A video link',
  link: 'A link', article: 'A link', reddit: 'A Reddit link', code: 'A code card', comic: 'A comic', toy: 'A toy', file: 'A file',
};

const gifOf = (m, fallbackTitle) => {
  const url = safeUrl(m.gif_url);
  if (!url) return null;
  return { title: clamp(oneLine(m.gif_title || fallbackTitle) || 'GIF', 120), url };
};

/**
 * Normalize one endpoint row. `channel` = the stream it came from ('chat' | 'spectating').
 * opts: { main, side, files, thoughts, secret, aiSenderIds:Set }. Returns a whitelisted line:
 * { at, channel:'main'|'side'|'dm', kind:'line'|'announcement'|'gif'|'file'|'note', who?, ai?,
 *   to?, text?, hidden?, thought?, gif?, file? } or null.
 */
export function normalizeRow(m, channel, opts = {}) {
  if (!m || typeof m !== 'object') return null;
  const at = tsOf(m);
  if (at == null) return null;
  const hidden = !!m.hidden_by_reports;
  const aiMark = !opts.secret && opts.aiSenderIds && m.sender_id != null && opts.aiSenderIds.has(m.sender_id);

  if (channel === 'spectating') {
    if (!opts.side || opts.secret) return null;
    if (m.is_watch_quip || m.is_stream_quip) return null;
    const who = nameOf(m.sender, m.sender_name);
    if (hidden) return { at, channel: 'side', kind: 'line', who, hidden: true };
    const text = clamp(oneLine(m.message), 4000);
    const gif = gifOf(m, text);
    if (!text && !gif) return null;
    return { at, channel: 'side', kind: text ? 'line' : 'gif', who, ...(text ? { text } : {}), ...(gif ? { gif } : {}) };
  }

  const type = m.type;
  if (type === 'private_hidden') return null; // another seat's DM: never ours to export

  if (type === 'private' || type === 'secret') {
    if (!opts.main) return null;
    const text = clamp(oneLine(m.message), 4000);
    if (text === '[Private message sent]') return null;
    const toList = (Array.isArray(m.recipients) ? m.recipients : Array.isArray(m.recipient_names) ? m.recipient_names : [])
      .map((n) => clamp(oneLine(n), 60)).filter(Boolean).slice(0, 12);
    const base = { at, channel: 'dm', kind: 'line', who: nameOf(m.sender, m.sender_name), ...(aiMark ? { ai: true } : {}), ...(toList.length ? { to: toList } : {}) };
    if (hidden) return { ...base, hidden: true };
    if (!text) return null;
    return { ...base, text };
  }

  // (a row's own data can carry a `type` key that the endpoint's spread lets win, so a
  // shared card is also known by its content_type)
  const isShared = type === 'shared_content' || (type !== 'public' && type !== 'announcement' && typeof m.content_type === 'string');
  if (isShared) {
    const who = nameOf(m.sender_name, m.sender);
    const ct = String(m.content_type || '').toLowerCase();
    const url = safeUrl(m.url);
    const isGif = !!m.is_reaction_gif || ct === 'gif' || !!m.gif_url || (!!url && /\.gif(?:[?#]|$)/i.test(url));
    if (isGif) {
      if (!opts.main) return null;
      if (hidden) return { at, channel: 'main', kind: 'line', who, hidden: true };
      const gurl = safeUrl(m.gif_url) || url;
      if (!gurl) return null;
      return { at, channel: 'main', kind: 'gif', who, gif: { title: clamp(oneLine(m.title || m.gif_title || m.description) || 'GIF', 120), url: gurl } };
    }
    if (ct === 'note' && !url) {
      if (!opts.main) return null;
      if (hidden) return { at, channel: 'main', kind: 'line', who, hidden: true };
      const text = clamp(oneLine(m.description || m.title), 1000);
      return text ? { at, channel: 'main', kind: 'note', who, text } : null;
    }
    if (!opts.files) return null;
    if (hidden) return { at, channel: 'main', kind: 'line', who, hidden: true };
    const name = clamp(oneLine(m.filename || m.title) || FILE_LABELS[ct] || 'A file', 120);
    const summary = clamp(oneLine(m.description), 240);
    return { at, channel: 'main', kind: 'file', who, file: { name, ...(summary ? { summary } : {}), ...(url ? { url } : {}) } };
  }

  const isAnnouncement = type === 'announcement' || (type !== 'public' && !('sender' in m));
  if (isAnnouncement) {
    if (!opts.main) return null;
    // any row id can be reported (moderationReports takes a bare target_id), and the reader
    // serves a hidden card's text as it was: masked here like every other line (review 09-29)
    if (hidden) return { at, channel: 'main', kind: 'announcement', hidden: true };
    // the two-text pattern: the page shows page_message when a card carries one (AiPower)
    const text = clamp(oneLine(m.page_message || m.message), 2000);
    return text ? { at, channel: 'main', kind: 'announcement', text } : null;
  }

  // a line someone said in the main chat (a person, an AI, the host)
  if (!opts.main) return null;
  const who = nameOf(m.sender, m.sender_name);
  if (hidden) return { at, channel: 'main', kind: 'line', who, ...(aiMark ? { ai: true } : {}), hidden: true };
  const text = clamp(oneLine(m.message), 4000);
  const gif = gifOf(m, text);
  if (!text && !gif) return null;
  // a string only: some lanes keep a structured `thoughts` (an explorer journal's array)
  const thought = opts.thoughts && !opts.secret && typeof m.thoughts === 'string' ? clamp(oneLine(m.thoughts), 2000) : '';
  return {
    at, channel: 'main', kind: text ? 'line' : 'gif', who,
    ...(aiMark ? { ai: true } : {}),
    ...(text ? { text } : {}), ...(gif ? { gif } : {}), ...(thought ? { thought } : {}),
  };
}

// ── paging ──────────────────────────────────────────────────────────────────────
const abortError = () => { const e = new Error('Export cancelled'); e.name = 'AbortError'; return e; };

async function pageStream({ api, gameId, viewerId, type, fromMs, toMs, cap, maxPages, opts, signal, onPage }) {
  const lines = [];
  const seen = new Set();
  let before = null;
  let pages = 0;
  let trimmed = false;
  let stoppedEarly = false;
  for (;;) {
    if (signal && signal.aborted) throw abortError();
    if (pages >= maxPages) { stoppedEarly = true; break; }
    // eslint-disable-next-line no-await-in-loop
    const res = await api.getMessages(gameId, viewerId || null, { before: before || undefined, limit: EXPORT_PAGE_SIZE, type });
    pages += 1;
    const msgs = res && Array.isArray(res.messages) ? res.messages : [];
    let oldest = Infinity;
    // the page is oldest-first; walk it newest-first so a cap keeps the NEWEST lines
    const kept = [];
    for (let i = msgs.length - 1; i >= 0; i -= 1) {
      const m = msgs[i];
      if (!m || (m.id != null && seen.has(m.id))) continue;
      if (m.id != null) seen.add(m.id);
      const t = tsOf(m);
      if (t != null && t < oldest) oldest = t;
      if (t == null) continue;
      if (fromMs != null && t < fromMs) continue;
      if (toMs != null && t > toMs) continue;
      const line = normalizeRow(m, type, opts);
      if (line) kept.push(line);
    }
    kept.reverse();
    lines.unshift(...kept);
    if (onPage) onPage(kept.length);
    if (lines.length > cap) { trimmed = true; break; }
    // an empty page that still says has_more (a whole page of other seats' secret DMs, which
    // the reader drops, so no oldest_id comes back) is a stop the file must admit to
    if (!msgs.length) { stoppedEarly = !!(res && res.has_more); break; }
    if (!res.has_more) break;
    if (fromMs != null && oldest < fromMs) break; // everything older is out of range
    const next = res.oldest_id || (msgs[0] && msgs[0].id) || null;
    if (!next || next === before) { stoppedEarly = !!res.has_more; break; }
    before = next;
  }
  return { lines, trimmed, stoppedEarly, pages };
}

/**
 * Gather the chosen lines, newest `cap` at most.
 * @param {object} o
 * @param {object} o.api        has getMessages(gameId, playerId, { before, limit, type }) (chatGameApi)
 * @param {string} o.gameId
 * @param {string} [o.viewerId] the viewer's seat id (the api proves it with the stored secret)
 * @param {object} o.include    { main, side, files, thoughts }
 * @param {object} o.bounds     { fromMs, toMs } from rangeBounds
 * @param {boolean} [o.secret]  competitive / imposter: no side chat, no thoughts, no AI marks
 * @param {Set} [o.aiSenderIds] seat ids the page already shows as AIs (for the "(AI)" mark only)
 * @returns {Promise<{ lines, trimmed, stoppedEarly, pages }>}
 */
export async function gatherChat({
  api, gameId, viewerId = null, include = EXPORT_DEFAULTS, bounds = { fromMs: null, toMs: null },
  secret = false, aiSenderIds = null, cap = EXPORT_LINE_CAP, maxPages = EXPORT_MAX_PAGES, onProgress = null, signal = null,
}) {
  if (!api || typeof api.getMessages !== 'function') throw new Error('No messages reader');
  if (!gameId) throw new Error('No room');
  const opts = {
    main: !!include.main, side: !!include.side && !secret, files: !!include.files,
    thoughts: !!include.thoughts && !secret, secret: !!secret, aiSenderIds: secret ? null : aiSenderIds,
  };
  const { fromMs = null, toMs = null } = bounds || {};
  let count = 0;
  let pages = 0;
  const onPage = (n) => { count += n; pages += 1; if (onProgress) onProgress({ lines: count, pages }); };
  const streams = [];
  if (opts.main || opts.files) streams.push('chat');
  if (opts.side) streams.push('spectating');
  const results = [];
  for (const type of streams) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await pageStream({ api, gameId, viewerId, type, fromMs, toMs, cap, maxPages, opts, signal, onPage }));
  }
  const merged = results.flatMap((r, s) => r.lines.map((l, i) => ({ l, s, i })));
  merged.sort((a, b) => (a.l.at - b.l.at) || (a.s - b.s) || (a.i - b.i));
  let lines = merged.map((x) => x.l);
  let trimmed = results.some((r) => r.trimmed);
  if (lines.length > cap) { lines = lines.slice(lines.length - cap); trimmed = true; }
  return { lines, trimmed, stoppedEarly: results.some((r) => r.stoppedEarly), pages: results.reduce((n, r) => n + r.pages, 0) };
}

// ── formatting ─────────────────────────────────────────────────────────────────
const fmt = (opts, timeZone) => new Intl.DateTimeFormat('en-US', { ...opts, ...(timeZone ? { timeZone } : {}) });
const timeOf = (ms, tz) => nbspToSpace(fmt({ hour: 'numeric', minute: '2-digit' }, tz).format(new Date(ms)));
const dayHead = (ms, tz) => nbspToSpace(fmt({ weekday: 'short', month: 'short', day: 'numeric' }, tz).format(new Date(ms)));
const dayKey = (ms, tz) => fmt({ year: 'numeric', month: '2-digit', day: '2-digit' }, tz).format(new Date(ms));
const monthDay = (ms, tz) => nbspToSpace(fmt({ month: 'short', day: 'numeric' }, tz).format(new Date(ms)));
const yearOf = (ms, tz) => fmt({ year: 'numeric' }, tz).format(new Date(ms));

/** "Sep 22 to Sep 29, 2026" (years on both ends when they differ). */
export function rangeLabel(fromMs, toMs, tz) {
  if (fromMs == null || toMs == null) return '';
  const ya = yearOf(fromMs, tz);
  const yb = yearOf(toMs, tz);
  if (dayKey(fromMs, tz) === dayKey(toMs, tz)) return `${monthDay(toMs, tz)}, ${yb}`;
  return ya === yb ? `${monthDay(fromMs, tz)} to ${monthDay(toMs, tz)}, ${yb}` : `${monthDay(fromMs, tz)}, ${ya} to ${monthDay(toMs, tz)}, ${yb}`;
}

const RANGE_WORDS = { week: 'last 7 days', all: 'everything', custom: 'custom dates' };
const includesWords = (inc, secret) => [
  inc.main && 'main chat',
  inc.side && !secret && 'side chat',
  inc.files && 'files as links',
  inc.thoughts && !secret && 'AI thoughts',
].filter(Boolean);

// Markdown: names and link text are escaped; a tag-looking "<" in chat text is escaped so a
// previewer that renders HTML can't run someone's message ("<3" stays as it is). Square
// brackets in chat text are escaped too (review 09-29): someone else's `![](https://…)` or
// `[x](…)` stays text, so opening the file in a previewer never loads a stranger's picture
// (a tracking pixel) or dresses up a link. The file's OWN links (GIFs, files) stay links.
const mdName = (s) => String(s).replace(/([\\`*_[\]<>#|])/g, '\\$1');
const mdText = (s) => String(s).replace(/\\/g, '\\\\').replace(/([[\]])/g, '\\$1').replace(/<(?=[A-Za-z/!?])/g, '\\<');
const mdLinkText = (s) => String(s).replace(/([\\[\]`*_<>])/g, '\\$1');

function mdLine(l, tz, roomName) {
  const t = timeOf(l.at, tz);
  if (l.kind === 'announcement') return `_${mdName(roomName)} · ${t}:_ ${l.hidden ? HIDDEN_LINE : mdText(l.text)}`;
  const who = `**${mdName(l.who)}**${l.ai ? ' (AI)' : ''}`;
  const head = l.channel === 'dm'
    ? `${who} (DM${l.to && l.to.length ? ` to ${l.to.map(mdName).join(', ')}` : ''}) · ${t}:`
    : `${who} · ${t}:`;
  let body;
  if (l.hidden) body = HIDDEN_LINE;
  else if (l.kind === 'file') {
    const f = l.file;
    body = `shared ${f.url ? `[${mdLinkText(f.name)}](${f.url})` : mdText(f.name)}${f.summary ? `: ${mdText(f.summary)}` : ''}`;
  } else {
    body = [l.text ? mdText(l.text) : '', l.gif ? `[GIF: ${mdLinkText(l.gif.title)}](${l.gif.url})` : ''].filter(Boolean).join(' ');
  }
  let out = `${l.channel === 'side' ? '> Side chat · ' : ''}${head} ${body}`;
  if (l.thought) out += `  \n💭 _${mdName(l.who)} thought:_ ${mdText(l.thought)}`;
  return out;
}

/** The file header's facts, shared by both formats. */
function headerFacts(result, meta) {
  const tz = meta.timeZone;
  const now = meta.now instanceof Date ? meta.now.getTime() : (meta.now || Date.now());
  const b = meta.bounds || {};
  const first = result.lines.length ? result.lines[0].at : null;
  const from = b.fromMs != null ? b.fromMs : first;
  const to = b.toMs != null ? Math.min(b.toMs, now) : now;
  return {
    from, to, now,
    label: from != null ? rangeLabel(from, to, tz) : '',
    words: RANGE_WORDS[meta.range] || RANGE_WORDS.week,
    includes: includesWords(meta.include || EXPORT_DEFAULTS, !!meta.secret),
  };
}

const trimNote = (result, cap) => [
  result.trimmed ? `Trimmed: this is the newest ${cap.toLocaleString('en-US')} lines. Use Custom dates for older ones.` : '',
  result.stoppedEarly ? 'Stopped early: this room is very long. Use Custom dates for older lines.' : '',
].filter(Boolean);

/** Markdown for a gathered result. meta: { roomName, range, include, bounds, secret, now, timeZone, cap } */
export function toMarkdown(result, meta = {}) {
  const tz = meta.timeZone;
  const room = clamp(oneLine(meta.roomName), 80) || 'This room';
  const cap = meta.cap || EXPORT_LINE_CAP;
  const h = headerFacts(result, meta);
  const n = result.lines.length;
  const out = [
    `# ${mdName(room)}: chat export`,
    '',
    `- Range: ${h.label ? `${h.label} (${h.words})` : h.words}`,
    `- Lines: ${n.toLocaleString('en-US')}`,
    `- Includes: ${h.includes.join(', ') || 'nothing'}`,
    `- Exported: ${new Date(h.now).toISOString().slice(0, 10)}`,
    `- ${EXPORT_PRIVACY_LINE}`,
  ];
  for (const note of trimNote(result, cap)) out.push('', `> ${note}`);
  if (!n) out.push('', 'Nothing in this range.');
  let day = null;
  for (const l of result.lines) {
    const k = dayKey(l.at, tz);
    if (k !== day) { day = k; out.push('', `## ${dayHead(l.at, tz)}`); }
    out.push('', mdLine(l, tz, room));
  }
  out.push('', '---', '', EXPORT_FOOTER);
  return `${out.join('\n')}\n`;
}

/** JSON for a gathered result: whitelisted keys only (never an id). */
export function toJson(result, meta = {}) {
  const room = clamp(oneLine(meta.roomName), 80) || 'This room';
  const cap = meta.cap || EXPORT_LINE_CAP;
  const h = headerFacts(result, meta);
  const inc = meta.include || EXPORT_DEFAULTS;
  const secret = !!meta.secret;
  const doc = {
    room,
    exported_at: new Date(h.now).toISOString(),
    range: {
      label: h.label || h.words,
      kind: meta.range || 'week',
      from: h.from != null ? new Date(h.from).toISOString() : null,
      to: new Date(h.to).toISOString(),
    },
    includes: {
      main_chat: !!inc.main,
      ...(secret ? {} : { side_chat: !!inc.side }),
      files_as_links: !!inc.files,
      ...(secret ? {} : { ai_thoughts: !!inc.thoughts }),
    },
    line_count: result.lines.length,
    trimmed: !!result.trimmed,
    ...(result.stoppedEarly ? { stopped_early: true } : {}),
    notes: [EXPORT_PRIVACY_LINE, ...trimNote(result, cap)],
    lines: result.lines.map((l) => {
      const o = { at: new Date(l.at).toISOString(), channel: l.channel };
      if (l.kind === 'announcement') {
        o.kind = 'announcement';
        if (l.hidden) { o.text = HIDDEN_LINE; o.hidden = true; } else o.text = l.text;
        return o;
      }
      o.who = l.who;
      if (l.ai) o.ai = true;
      if (l.to) o.to = l.to;
      if (l.hidden) { o.text = HIDDEN_LINE; o.hidden = true; return o; }
      if (l.kind !== 'line') o.kind = l.kind;
      if (l.text) o.text = l.text;
      if (l.gif) o.gif = { title: l.gif.title, url: l.gif.url };
      if (l.file) o.file = { name: l.file.name, ...(l.file.summary ? { summary: l.file.summary } : {}), ...(l.file.url ? { url: l.file.url } : {}) };
      if (l.thought) o.thought = l.thought;
      return o;
    }),
    footer: EXPORT_FOOTER,
  };
  return `${JSON.stringify(doc, null, 2)}\n`;
}

/** A safe file name: "the-claude-house-chat-2026-09-29.md". */
export function exportFileName(roomName, format = 'md', now = new Date()) {
  const slug = oneLine(roomName).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'room';
  const d = (now instanceof Date ? now : new Date(now)).toISOString().slice(0, 10);
  return `${slug}-chat-${d}.${format === 'json' ? 'json' : 'md'}`;
}

/** Save text as a file in the browser (Blob + a temporary <a download>). */
export function downloadText(text, fileName, format = 'md') {
  const type = format === 'json' ? 'application/json;charset=utf-8' : 'text/markdown;charset=utf-8';
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // generous: a slow disk / Safari's save sheet can still be reading the blob after 1s
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
